/**
 * Lista commesse — colonne estese (priority, health, invoicedAmount, contractValue)
 * + toolbar di filtro per priority, health, customer.
 * Filtri e paginazione sono persistiti nei search params dell'URL.
 */
import { createFileRoute } from '@tanstack/react-router';
import {
  Badge, Button, Card, Group, Loader, Progress, Select, SimpleGrid, Stack, Text, TextInput, ThemeIcon,
} from '@mantine/core';
import {
  IconAlertTriangle, IconCircleCheck, IconDownload, IconFolders, IconPlus, IconProgressAlert, IconSearch, IconSitemap, IconTimeline,
  type Icon,
  IconBuildingFactory2,
} from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { useDebouncedValue } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import { DataTable, type DataTableColumn } from 'mantine-datatable';
import { notifications } from '@mantine/notifications';
import { notifyError } from '@/shared/lib/notify';
import { useJsonApiTable } from '@/shared/components/DataTable/useJsonApiTable';
import type { TableFilters } from '@/shared/components/DataTable/types';
import { QueryErrorAlert } from '@/shared/components/QueryErrorAlert';
import { MaePageHead } from '@/shared/desktop/MaeShell';
import { ExternalSyncNotice } from '@/features/admin/ExternalSyncNotice';
import { fetchList } from '@/shared/api/jsonapi/client';
import { downloadCsv, getByPath, type CsvColumn } from '@/shared/lib/exportCsv';
import { formatDate, formatMoney, formatNumber } from '@/shared/lib/format';
import type { FilterClause, ListQuery } from '@/shared/api/jsonapi/types';
import { DesktopProjects } from '@/features/projects/desktop/DesktopProjects';
import { MobileProjects } from '@/features/projects/mobile/MobileProjects';
import { useIsMobileShell } from '@/shared/mobile/useIsMobileShell';
import { InlineLoader } from '@/shared/components/LoadingState';
import { FavoriteStar } from '@/features/projects/FavoriteStar';
import { HealthBadge, PriorityBadge } from '@/features/projects/HealthBadge';
import { NewProjectModal } from '@/features/projects/NewProjectModal';
import { canSeeProjectFinancials } from '@/features/projects/projectFinancials';
import { useAbility } from '@/app/providers/AbilityProvider';
import type { Denormalized } from '@/shared/api/jsonapi/parser';

interface Project extends Denormalized {
  type: 'projects';
  code?: string;
  name?: string;
  description?: string;
  phase?: string;
  startDate?: string;
  endDate?: string;
  budgetRevenue?: number;
  priority?: string;
  healthStatus?: string;
  invoicedAmount?: number;
  contractValue?: number;
  currency?: string;
  wbsApprovedAt?: string | null;
  customer?: { id: string; type?: string; client?: { organization?: string; name?: string; firstName?: string; lastName?: string } };
}

import { statusColor } from '@/shared/lib/statusColors';
import { phaseLabel } from '@/features/projects/phase';
import { WBS_STATE_LABEL, wbsListFilter, type WbsState } from '@/features/projects/wbsState';
import { customerLabel, tenantClientOptions, type TenantClientRow } from '@/features/projects/customerLabel';

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Bassa' },
  { value: 'medium', label: 'Media' },
  { value: 'high', label: 'Alta' },
  { value: 'urgent', label: 'Urgente' },
];

const HEALTH_OPTIONS = [
  { value: 'green', label: 'On track' },
  { value: 'yellow', label: 'A rischio' },
  { value: 'red', label: 'Critico' },
];

/* «Senza WBS» non c'e': il builder non sa negare `has(…)`. */
const WBS_OPTIONS: { value: WbsState | 'any'; label: string }[] = [
  { value: 'any', label: 'Con WBS' },
  { value: 'draft', label: `WBS: ${WBS_STATE_LABEL.draft}` },
  { value: 'active', label: `WBS: ${WBS_STATE_LABEL.active}` },
  { value: 'closed', label: `WBS: ${WBS_STATE_LABEL.closed}` },
];

const PAGE_SIZES = [10, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;
const EXPORT_PAGE_SIZE = 100;
const EXPORT_MAX_ROWS = 2000;

/** Search params della lista commesse: param assente = default (URL pulito). */
interface ProjectsSearch {
  /** `true` = modale "Nuova commessa" aperta. */
  new?: true;
  q?: string;
  priority?: string;
  health?: string;
  customer?: string;
  /** Solo i progetti con una WBS, eventualmente in uno stato. */
  wbs?: WbsState | 'any';
  /** Vista tabella MRT (`table`); assente = card-grid brand MaeDesign. */
  view?: 'table';
  /** 1-based; assente = pagina 1. */
  page?: number;
  /** Assente = 25. */
  pageSize?: number;
}

export const Route = createFileRoute('/_app/projects/')({
  validateSearch: (s: Record<string, unknown>): ProjectsSearch => ({
    // `?new=1` apre la modale "Nuova commessa": la creazione non è più una pagina, ma
    // resta deep-linkabile (ed è dove reindirizza la vecchia rotta /projects/new).
    new: s.new === true || s.new === 'true' || s.new === 1 || s.new === '1' ? true : undefined,
    q: typeof s.q === 'string' && s.q.trim() ? s.q.trim() : undefined,
    priority: typeof s.priority === 'string' && PRIORITY_OPTIONS.some((o) => o.value === s.priority) ? s.priority : undefined,
    health: typeof s.health === 'string' && HEALTH_OPTIONS.some((o) => o.value === s.health) ? s.health : undefined,
    customer: typeof s.customer === 'string' && s.customer ? s.customer : undefined,
    wbs: WBS_OPTIONS.find((o) => o.value === s.wbs)?.value,
    view: s.view === 'table' ? 'table' : undefined,
    page: typeof s.page === 'number' && s.page > 1 ? Math.floor(s.page) : undefined,
    pageSize: typeof s.pageSize === 'number' && s.pageSize > 0 && s.pageSize !== DEFAULT_PAGE_SIZE ? Math.floor(s.pageSize) : undefined,
  }),
  component: ProjectsPage,
});

/**
 * Mobile = schermata dedicata; desktop: card-grid brand (default) o tabella MRT (`?view=table`).
 *
 * La modale "Nuova commessa" è montata **qui**, sopra tutte le varianti: il suo stato vive
 * nel search param `?new=1`, così i tre punti d'ingresso (card-grid, tabella, mobile) e i
 * link diretti la aprono senza duplicare il form.
 */
function ProjectsPage() {
  const { view, new: newOpen } = Route.useSearch();
  const navigate = Route.useNavigate();
  const isMobile = useIsMobileShell();
  const closeNew = () => void navigate({ search: (p) => ({ ...p, new: undefined }), replace: true });
  return (
    <>
      {isMobile ? <MobileProjects /> : view === 'table' ? <ProjectsListPage /> : <DesktopProjects />}
      <NewProjectModal
        opened={newOpen === true}
        onClose={closeNew}
        onCreated={(id) => {
          closeNew();
          if (id) void navigate({ to: '/projects/$projectId', params: { projectId: id } });
        }}
      />
    </>
  );
}

const PROJECT_CSV_COLUMNS: Array<CsvColumn<Project>> = [
  { header: 'Codice', accessor: (r) => r.code },
  { header: 'Nome', accessor: (r) => r.name },
  { header: 'Cliente', accessor: (r) => customerLabel(r.customer) ?? getByPath(r, 'customer.client.organization') },
  { header: 'Fase', accessor: (r) => phaseLabel(r.phase) },
  { header: 'Priorità', accessor: (r) => r.priority },
  { header: 'Health', accessor: (r) => r.healthStatus },
  { header: 'Inizio', accessor: (r) => r.startDate },
  { header: 'Fine', accessor: (r) => r.endDate },
  { header: 'Fatturato', accessor: (r) => r.invoicedAmount },
  { header: 'Contratto', accessor: (r) => r.contractValue },
  { header: 'Budget ricavi', accessor: (r) => r.budgetRevenue },
  { header: 'Valuta', accessor: (r) => r.currency },
];

const MONEY_HEADERS = new Set(['Fatturato', 'Contratto', 'Budget ricavi']);
// Colonne che leggono importi: il sistema non li manda a chi non vede i conti.
const MONEY_ACCESSORS = new Set(['progress', 'invoicedAmount', 'contractValue', 'budgetRevenue']);

function ProjectsListPage() {
  const navigate = Route.useNavigate();
  const financials = canSeeProjectFinancials(useAbility());
  const search = Route.useSearch();
  const [exporting, setExporting] = useState(false);

  const page = search.page ?? 1;
  const pageSize = search.pageSize ?? DEFAULT_PAGE_SIZE;
  const priorityF = search.priority ?? null;
  const healthF = search.health ?? null;
  const customerF = search.customer ?? null;
  const wbsF = search.wbs ?? null;
  const wbsFilter = useMemo(() => (wbsF ? wbsListFilter(wbsF) : undefined), [wbsF]);

  const updateSearch = (patch: Partial<ProjectsSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });

  // Ricerca per nome: input reattivo + debounce → URL (?q=) → filtro server.
  const [qInput, setQInput] = useState(search.q ?? '');
  const [qDebounced] = useDebouncedValue(qInput.trim(), 350);
  useEffect(() => {
    if ((qDebounced || undefined) !== search.q) updateSearch({ q: qDebounced || undefined, page: undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qDebounced]);

  // Filtri lato server: i controlli della pagina (ricerca, priorità, health, cliente) diventano
  // le chiavi del filtro, che `useJsonApiTable` traduce in `like`.
  const filters = useMemo<TableFilters>(() => {
    const f: TableFilters = {};
    if (search.q) f.name = search.q;
    if (priorityF) f.priority = priorityF;
    if (healthF) f.healthStatus = healthF;
    if (customerF) f['customer.id'] = customerF;
    return f;
  }, [search.q, priorityF, healthF, customerF]);

  const { data, isLoading, isError, error, refetch } = useJsonApiTable<Project>('projects', {
    page: { page, pageSize },
    sort: null,
    filters,
    baseFilter: wbsFilter,
    include: ['customer.client'],
  });

  // BE v1.20.12: il filtro cliente lavora sui tenant-clients (CRM), non più sui tenants.
  const tenantClientsQ = useQuery({
    queryKey: ['tenant-clients-for-list-filter'],
    queryFn: () => fetchList<TenantClientRow>('tenant-clients', { include: ['client'], page: { size: 100 } }),
  });
  const customerOpts = tenantClientOptions(tenantClientsQ.data?.data ?? []);

  const exportCsv = async () => {
    setExporting(true);
    try {
      // Ricostruisce la stessa query della tabella (filtri correnti) e scarica
      // tutte le pagine fino al cap (stesso mapping like di useJsonApiTable).
      const filter: Record<string, FilterClause> = { ...(wbsFilter ?? {}) };
      for (const [field, value] of Object.entries(filters)) {
        if (value) filter[field] = { like: value };
      }

      const rows: Project[] = [];
      for (let p = 1; rows.length < EXPORT_MAX_ROWS; p++) {
        const query: ListQuery = {
          page: { number: p, size: EXPORT_PAGE_SIZE },
          filter: Object.keys(filter).length ? filter : undefined,
          include: ['customer.client'],
        };
        const res = await fetchList<Project>('projects', query);
        rows.push(...res.data);
        if (res.data.length < EXPORT_PAGE_SIZE) break;
      }
      const truncated = rows.length >= EXPORT_MAX_ROWS;
      const csvColumns = financials ? PROJECT_CSV_COLUMNS : PROJECT_CSV_COLUMNS.filter((c) => !MONEY_HEADERS.has(c.header));
      downloadCsv(`progetti-${new Date().toISOString().slice(0, 10)}`, csvColumns, rows.slice(0, EXPORT_MAX_ROWS));
      notifications.show({
        color: truncated ? 'orange' : 'green',
        message: truncated
          ? `Esportate le prime ${EXPORT_MAX_ROWS} righe (il dataset è più grande: filtra per esportare il resto).`
          : `Esportate ${rows.length} righe.`,
      });
    } catch (e) {
      notifyError(e, 'Errore export');
    } finally {
      setExporting(false);
    }
  };

  const columns = useMemo<DataTableColumn<Project>[]>(() => ([
    {
      accessor: 'isFavorite',
      title: '★',
      width: 50,
      render: (row) => <FavoriteStar projectId={row.id} />,
    },
    { accessor: 'code', title: 'Codice', width: 110 },
    { accessor: 'name', title: 'Nome' },
    {
      accessor: 'customer',
      title: 'Cliente',
      render: (row) => <Text size="sm">{customerLabel(row.customer) ?? '—'}</Text>,
    },
    {
      accessor: 'phase',
      title: 'Fase',
      width: 130,
      render: (row) => (row.phase ? <Badge color={statusColor(row.phase)}>{phaseLabel(row.phase)}</Badge> : '—'),
    },
    {
      accessor: 'wbsApprovedAt',
      title: 'WBS approvata',
      width: 130,
      /* Il trattino non dice «senza WBS»: dice «mai approvata». Lo stato si filtra sopra. */
      render: (row) => <Text size="sm" c={row.wbsApprovedAt ? undefined : 'dimmed'}>{row.wbsApprovedAt ? formatDate(row.wbsApprovedAt) : '—'}</Text>,
    },
    {
      accessor: 'priority',
      title: 'Priorità',
      width: 110,
      render: (row) => <PriorityBadge value={row.priority} />,
    },
    {
      accessor: 'healthStatus',
      title: 'Health',
      width: 120,
      render: (row) => <HealthBadge value={row.healthStatus} />,
    },
    {
      accessor: 'progress',
      title: 'Avanzamento',
      width: 150,
      render: (row) => {
        const inv = row.invoicedAmount ?? 0;
        const base = row.contractValue ?? row.budgetRevenue ?? 0;
        if (!base) return <Text size="xs" c="dimmed">—</Text>;
        const pct = Math.min(100, Math.round((inv / base) * 100));
        const color = pct >= 90 ? 'green' : pct >= 50 ? 'blue' : 'orange';
        return (
          <div style={{ minWidth: 120 }}>
            <Group justify="space-between" gap={4} mb={3}>
              <Text size="xs" c="dimmed">fatturato</Text>
              <Text size="xs" fw={600} style={{ fontVariantNumeric: 'tabular-nums' }}>{pct}%</Text>
            </Group>
            <Progress value={pct} size="sm" color={color} radius="xl" />
          </div>
        );
      },
    },
    {
      accessor: 'startDate',
      title: 'Inizio',
      width: 110,
      render: (row) => <Text size="sm">{formatDate(row.startDate)}</Text>,
    },
    {
      accessor: 'endDate',
      title: 'Fine',
      width: 110,
      render: (row) => <Text size="sm">{formatDate(row.endDate)}</Text>,
    },
    {
      accessor: 'invoicedAmount',
      title: 'Fatturato',
      width: 140,
      textAlign: 'right',
      render: (row) => <Text size="sm">{formatMoney(row.invoicedAmount, row.currency ?? 'EUR')}</Text>,
    },
    {
      accessor: 'contractValue',
      title: 'Contratto',
      width: 140,
      textAlign: 'right',
      render: (row) => <Text size="sm">{formatMoney(row.contractValue, row.currency ?? 'EUR')}</Text>,
    },
    {
      accessor: 'budgetRevenue',
      title: 'Budget ricavi',
      width: 130,
      textAlign: 'right',
      render: (row) => <Text size="sm">{formatMoney(row.budgetRevenue, row.currency ?? 'EUR')}</Text>,
    },
  ] as DataTableColumn<Project>[]).filter((c) => financials || !MONEY_ACCESSORS.has(String(c.accessor))), [financials]);

  return (
    <Stack gap="lg">
      <MaePageHead
        title="Progetti"
        sub="Gestione progetti, budget e team"
        icon={<IconBuildingFactory2 size={23} />}
        actions={
          <>
            <Button variant="light" leftSection={<IconTimeline size={16} />} onClick={() => void navigate({ to: '/projects/gantt' })}>
              Gantt overview
            </Button>
            <Button variant="light" leftSection={<IconSitemap size={16} />} onClick={() => void navigate({ to: '/projects/wbs' })}>
              Andamento WBS
            </Button>
            <Button leftSection={<IconPlus size={16} />} onClick={() => void navigate({ to: '/projects', search: (p) => ({ ...p, new: true }) })}>
              Nuovo progetto
            </Button>
          </>
        }
      />

      <ExternalSyncNotice resourceType="projects" records={data?.data} />

      <ProjectsKpis />

      <Card withBorder padding="sm" radius="lg">
        <Group gap="sm" wrap="wrap" align="flex-end">
          <TextInput
            placeholder="Cerca per nome…"
            leftSection={<IconSearch size={16} />}
            value={qInput}
            onChange={(e) => setQInput(e.currentTarget.value)}
            style={{ flex: 1, minWidth: 220 }}
          />
          <Select
            placeholder="Priorità"
            data={PRIORITY_OPTIONS}
            value={priorityF}
            onChange={(v) => updateSearch({ priority: v ?? undefined, page: undefined })}
            clearable
            w={170}
          />
          <Select
            placeholder="Health"
            data={HEALTH_OPTIONS}
            value={healthF}
            onChange={(v) => updateSearch({ health: v ?? undefined, page: undefined })}
            clearable
            w={170}
          />
          <Select
            placeholder="Cliente"
            data={customerOpts}
            value={customerF}
            onChange={(v) => updateSearch({ customer: v ?? undefined, page: undefined })}
            clearable
            searchable
            w={220}
          />
          <Select
            placeholder="WBS"
            data={WBS_OPTIONS}
            value={wbsF}
            onChange={(v) => updateSearch({ wbs: (v as WbsState | 'any' | null) ?? undefined, page: undefined })}
            clearable
            w={170}
          />
        </Group>
      </Card>

      {/* L'errore non è più una striscia dentro la toolbar della tabella: è l'avviso di casa,
          che dice cosa è andato storto e offre il riprova (regola 7). */}
      {isError && <QueryErrorAlert error={error} onRetry={() => void refetch()} />}
      <Group justify="flex-end" mb="sm">
        <Button
          size="xs"
          variant="default"
          leftSection={<IconDownload size={14} />}
          loading={exporting}
          disabled={isLoading || (data?.total ?? 0) === 0}
          onClick={() => void exportCsv()}
        >
          Esporta CSV
        </Button>
      </Group>
      <DataTable<Project>
        withTableBorder
        borderRadius="lg"
        shadow="sm"
        highlightOnHover
        striped
        minHeight={(data?.data.length ?? 0) === 0 ? 220 : undefined}
        columns={columns}
        records={(data?.data as Project[] | undefined) ?? []}
        fetching={isLoading}
        page={page}
        onPageChange={(p) => updateSearch({ page: p > 1 ? p : undefined })}
        totalRecords={data?.total ?? 0}
        recordsPerPage={pageSize}
        onRecordsPerPageChange={(n) => updateSearch({ pageSize: n !== DEFAULT_PAGE_SIZE ? n : undefined, page: undefined })}
        recordsPerPageOptions={PAGE_SIZES}
        recordsPerPageLabel="Righe per pagina"
        onRowClick={({ record }) => void navigate({ to: `/projects/${record.id}` })}
        /* Il vuoto e l'attesa non sono la stessa cosa (regola 7): finché si legge si mostra il
           caricamento, altrimenti si direbbe «nessun progetto» mentre la risposta è per strada. */
        emptyState={
          <div style={{ pointerEvents: 'auto' }}>
            {isLoading
              ? <InlineLoader label="Caricamento dei progetti…" minHeight={200} />
              : <Text c="dimmed" ta="center" py="xl">Nessun progetto</Text>}
          </div>
        }
      />
    </Stack>
  );
}

/** Conteggi globali (page.size:1 → meta.total). Resilienti: in errore mostrano "—". */
function useProjectCount(key: string, filter?: Record<string, FilterClause>) {
  return useQuery({
    queryKey: ['projects-kpi', key],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const res = await fetchList<Project>('projects', { page: { size: 1 }, filter });
      return res.total;
    },
  });
}

function ProjectsKpis() {
  const total = useProjectCount('total');
  const green = useProjectCount('green', { healthStatus: { eq: 'green' } });
  const yellow = useProjectCount('yellow', { healthStatus: { eq: 'yellow' } });
  const red = useProjectCount('red', { healthStatus: { eq: 'red' } });

  return (
    <SimpleGrid cols={{ base: 2, md: 4 }} spacing="md">
      <KpiCard label="Totai progetti" icon={IconFolders} color="blue" q={total} />
      <KpiCard label="On track" icon={IconCircleCheck} color="green" q={green} />
      <KpiCard label="A rischio" icon={IconProgressAlert} color="yellow" q={yellow} />
      <KpiCard label="Critiche" icon={IconAlertTriangle} color="red" q={red} />
    </SimpleGrid>
  );
}

function KpiCard({ label, icon: Icon, color, q }: {
  label: string;
  icon: Icon;
  color: string;
  q: { data?: number; isLoading: boolean; isError: boolean };
}) {
  return (
    <Card withBorder padding="md" radius="lg">
      <Group justify="space-between" wrap="nowrap">
        <div style={{ minWidth: 0 }}>
          <Text size="xs" c="dimmed">{label}</Text>
          {/* Spinner e non `…`: l'ellissi in un contatore si legge come un numero troncato. */}
          <Text fz="var(--fs-h1)" fw={700} lh={1.1} c={color === 'blue' ? undefined : `${color}.7`} component="div" mih={29}>
            {q.isLoading ? <Loader size="xs" color={color} mt={5} /> : q.isError ? '—' : formatNumber(q.data ?? 0)}
          </Text>
        </div>
        <ThemeIcon variant="light" color={color} size={40} radius="md"><Icon size={22} /></ThemeIcon>
      </Group>
    </Card>
  );
}
